// =============================================================================
// ACT PREP - UNIFIED DATA INDEX
// Master index combining all subjects: Math, English, Reading, Science
// =============================================================================

// Import subject-specific indexes
import * as mathIndex from './math/testBankIndex.js';
import * as mathPractice from './math/practiceIndex.js';
import * as englishIndex from './english/testBankIndex.js';
import * as readingIndex from './reading/testBankIndex.js';
import * as scienceIndex from './science/testBankIndex.js';

// =============================================================================
// SUBJECT DEFINITIONS
// =============================================================================
export const subjects = {
  math: {
    id: 'math',
    name: 'Mathematics',
    icon: '📐',
    color: 'amber',
    totalTime: 60,
    description: 'Algebra, geometry, statistics, and more',
    hasPracticeBank: true
  },
  english: {
    id: 'english',
    name: 'English',
    icon: '📝',
    color: 'blue',
    totalTime: 45,
    description: 'Grammar, punctuation, and rhetorical skills',
    hasPracticeBank: false
  },
  reading: {
    id: 'reading',
    name: 'Reading',
    icon: '📖',
    color: 'purple',
    totalTime: 40,
    description: 'Reading comprehension and analysis',
    hasPracticeBank: false
  },
  science: {
    id: 'science',
    name: 'Science',
    icon: '🔬',
    color: 'emerald',
    totalTime: 40,
    description: 'Scientific reasoning and data analysis',
    hasPracticeBank: false
  }
};

// =============================================================================
// CATEGORY DEFINITIONS BY SUBJECT
// =============================================================================
export const categoriesBySubject = {
  math: {
    A: { id: 'A', name: 'Algebra', description: 'Linear equations, systems, polynomials' },
    G: { id: 'G', name: 'Geometry', description: 'Shapes, angles, coordinate geometry' },
    F: { id: 'F', name: 'Functions', description: 'Function notation, graphs, transformations' },
    S: { id: 'S', name: 'Statistics & Probability', description: 'Data analysis, probability, distributions' },
    N: { id: 'N', name: 'Number & Quantity', description: 'Real/complex numbers, ratios, matrices' },
    IES: { id: 'IES', name: 'Integrating Essential Skills', description: 'Multi-step problems integrating skills' }
  },
  english: {
    POW: { id: 'POW', name: 'Production of Writing', description: 'Topic development, organization, unity, cohesion' },
    KLA: { id: 'KLA', name: 'Knowledge of Language', description: 'Word choice, style, tone consistency' },
    CSE: { id: 'CSE', name: 'Conventions of Standard English', description: 'Sentence structure, punctuation, usage' }
  },
  reading: {
    KID: { id: 'KID', name: 'Key Ideas & Details', description: 'Central ideas, summarizing, inferences' },
    CS: { id: 'CS', name: 'Craft & Structure', description: 'Word meaning, author purpose, text structure' },
    IKI: { id: 'IKI', name: 'Integration of Knowledge & Ideas', description: 'Claims, evidence, multiple texts' }
  },
  science: {
    IOD: { id: 'IOD', name: 'Interpretation of Data', description: 'Reading and interpreting data presentations' },
    SIN: { id: 'SIN', name: 'Scientific Investigation', description: 'Understanding experimental design' },
    EMI: { id: 'EMI', name: 'Evaluation of Models', description: 'Evaluating models, inferences, predictions' }
  }
};

// =============================================================================
// TEST FORMS BY SUBJECT
// =============================================================================
export const testFormsBySubject = {
  math: [
    { id: '2176CPRE', name: 'Form 2176CPRE', source: 'ACT Practice Test (Classic)', questions: 60, scored: 60, time: 60 },
    { id: '25MC1', name: 'Form 25MC1', source: 'Preparing for the ACT 2025-2026', questions: 45, scored: 41, time: 50 },
    { id: '25MC2', name: 'Form 25MC2', source: 'Official ACT Prep Guide 2025-2026', questions: 45, scored: 41, time: 50 },
    { id: '25MC3', name: 'Form 25MC3', source: 'Official ACT Prep Guide 2025-2026', questions: 45, scored: 41, time: 50 },
    { id: '25MC4', name: 'Form 25MC4', source: 'Official ACT Prep Guide 2025-2026', questions: 45, scored: 41, time: 50 },
    { id: '25MC5', name: 'Form 25MC5', source: 'ACT Practice Test 2 (2025-2026)', questions: 45, scored: 41, time: 50 },
    { id: 'J08', name: 'Form J08 (Oct 2025)', source: 'ACT October 2025 Release', questions: 41, scored: 41, time: 50 }
  ],
  english: [
    { id: 'english-25MC1', formCode: '25MC1', name: 'Form 25MC1', source: 'Official ACT Prep Guide 2025-2026', questions: 50, scored: 40, time: 35, hasFieldTest: true },
    { id: 'english-25MC4', formCode: '25MC4', name: 'Form 25MC4', source: 'Official ACT Prep Guide 2025-2026 (Practice Test 4)', questions: 50, scored: 40, time: 35, hasFieldTest: true },
    { id: 'english-J08', formCode: 'J08', name: 'Form J08', source: 'October 2025 ACT', questions: 40, scored: 40, time: 28, hasFieldTest: false }
  ],
  reading: [
    { id: '25MC1', name: 'Form 25MC1', source: 'Preparing for the ACT 2025-2026', questions: 36, scored: 27, time: 40, hasFieldTest: true },
    { id: 'J08', name: 'Form J08', source: 'ACT October 2025 Release', questions: 27, scored: 27, time: 35, hasFieldTest: false }
  ],
  science: [
    { id: '25MC1', name: 'Form 25MC1', source: 'Preparing for the ACT 2025-2026', questions: 40, scored: 34, time: 40, hasFieldTest: true },
    { id: 'J08', name: 'Form J08', source: 'ACT October 2025 Release', questions: 34, scored: 34, time: 35, hasFieldTest: false }
  ]
};

// =============================================================================
// ALL TEST FORMS (combined list)
// =============================================================================
export const allTestForms = [
  // Math (7 forms)
  { id: 'math-2176CPRE', subject: 'math', formCode: '2176CPRE', name: 'Math Form 2176CPRE (Classic)', questions: 60, scored: 60, time: 60 },
  { id: 'math-25MC1', subject: 'math', formCode: '25MC1', name: 'Math Form 25MC1', questions: 45, scored: 41, time: 50 },
  { id: 'math-25MC2', subject: 'math', formCode: '25MC2', name: 'Math Form 25MC2', questions: 45, scored: 41, time: 50 },
  { id: 'math-25MC3', subject: 'math', formCode: '25MC3', name: 'Math Form 25MC3', questions: 45, scored: 41, time: 50 },
  { id: 'math-25MC4', subject: 'math', formCode: '25MC4', name: 'Math Form 25MC4', questions: 45, scored: 41, time: 50 },
  { id: 'math-25MC5', subject: 'math', formCode: '25MC5', name: 'Math Form 25MC5', questions: 45, scored: 41, time: 50 },
  { id: 'math-J08', subject: 'math', formCode: 'J08', name: 'Math Form J08', questions: 41, scored: 41, time: 50 },
  // English (3 forms)
  { id: 'english-25MC1', subject: 'english', formCode: '25MC1', name: 'English Form 25MC1', questions: 50, scored: 40, time: 35 },
  { id: 'english-25MC4', subject: 'english', formCode: '25MC4', name: 'English Form 25MC4', questions: 50, scored: 40, time: 35 },
  { id: 'english-J08', subject: 'english', formCode: 'J08', name: 'English Form J08', questions: 40, scored: 40, time: 28 },
  // Reading
  { id: 'reading-25MC1', subject: 'reading', formCode: '25MC1', name: 'Reading Form 25MC1', questions: 36, scored: 27, time: 40 },
  { id: 'reading-J08', subject: 'reading', formCode: 'J08', name: 'Reading Form J08', questions: 27, scored: 27, time: 35 },
  // Science
  { id: 'science-25MC1', subject: 'science', formCode: '25MC1', name: 'Science Form 25MC1', questions: 40, scored: 34, time: 40 },
  { id: 'science-J08', subject: 'science', formCode: 'J08', name: 'Science Form J08', questions: 34, scored: 34, time: 35 }
];

// =============================================================================
// UNIFIED HELPER FUNCTIONS (auto-detect subject from formId)
// =============================================================================

/**
 * Get questions for a specific test form
 * @param {string} formId - Form identifier
 * @param {string} subject - Optional subject to help with lookup
 */
export function getTestQuestions(formId, subject = null) {
  // Try to auto-detect subject from formId
  if (formId.startsWith('math-') || formId === '25MC3' || formId === '25MC4') {
    return mathIndex.getTestQuestions?.(formId.replace('math-', '')) || [];
  }
  if (formId.startsWith('english-')) {
    return englishIndex.getTestQuestions?.(formId) || [];
  }
  if (formId.startsWith('reading-') || (subject === 'reading')) {
    return readingIndex.getTestQuestions?.(formId.replace('reading-', '')) || [];
  }
  if (formId.startsWith('science-') || (subject === 'science')) {
    return scienceIndex.getTestQuestions?.(formId.replace('science-', '')) || [];
  }
  
  // Fallback: try each subject
  if (subject === 'math') return mathIndex.getTestQuestions?.(formId) || [];
  if (subject === 'english') return englishIndex.getTestQuestions?.(formId) || [];
  if (subject === 'reading') return readingIndex.getTestQuestions?.(formId) || [];
  if (subject === 'science') return scienceIndex.getTestQuestions?.(formId) || [];
  
  return [];
}

/**
 * Get only scored questions for a test form
 */
export function getScoredQuestions(formId, subject = null) {
  const questions = getTestQuestions(formId, subject);
  return questions.filter(q => q.isScored !== false);
}

/**
 * Calculate scaled score for a form
 */
export function calculateTestScore(formId, rawScore, subject = null) {
  if (formId.startsWith('math-') || formId === '25MC3' || formId === '25MC4' || subject === 'math') {
    return mathIndex.calculateTestScore?.(formId.replace('math-', ''), rawScore) || rawScore;
  }
  if (formId.startsWith('english-') || subject === 'english') {
    return englishIndex.calculateTestScore?.(formId, rawScore) || rawScore;
  }
  if (formId.startsWith('reading-') || subject === 'reading') {
    return readingIndex.calculateTestScore?.(formId.replace('reading-', ''), rawScore) || rawScore;
  }
  if (formId.startsWith('science-') || subject === 'science') {
    return scienceIndex.calculateTestScore?.(formId.replace('science-', ''), rawScore) || rawScore;
  }
  return rawScore;
}

/**
 * Check if a form has real questions loaded
 */
export function hasRealQuestions(formId, subject = null) {
  const questions = getTestQuestions(formId, subject);
  return questions.length > 0 && questions.some(q => q.question && q.choices);
}

/**
 * Get form metadata
 */
export function getFormMetadata(formId, subject = null) {
  if (formId.startsWith('math-') || formId === '25MC3' || formId === '25MC4' || subject === 'math') {
    return mathIndex.getFormMetadata?.(formId.replace('math-', ''));
  }
  if (formId.startsWith('english-') || subject === 'english') {
    return englishIndex.getFormMetadata?.(formId);
  }
  if (formId.startsWith('reading-') || subject === 'reading') {
    return readingIndex.getFormMetadata?.(formId.replace('reading-', ''));
  }
  if (formId.startsWith('science-') || subject === 'science') {
    return scienceIndex.getFormMetadata?.(formId.replace('science-', ''));
  }
  return null;
}

/**
 * Get passages for a form (Reading/Science)
 */
export function getPassages(formId, subject = null) {
  if (formId.startsWith('english-') || subject === 'english') {
    return englishIndex.getPassages?.(formId) || [];
  }
  if (formId.startsWith('reading-') || subject === 'reading') {
    return readingIndex.getPassages?.(formId.replace('reading-', '')) || [];
  }
  if (formId.startsWith('science-') || subject === 'science') {
    return scienceIndex.getPassages?.(formId.replace('science-', '')) || [];
  }
  return [];
}

/**
 * Get test forms for a subject
 */
export function getTestForms(subject) {
  return testFormsBySubject[subject] || [];
}

/**
 * Get category name by ID and subject
 */
export function getCategoryName(categoryId, subject) {
  const categories = categoriesBySubject[subject];
  if (categories && categories[categoryId]) {
    return categories[categoryId].name;
  }
  return categoryId;
}

// =============================================================================
// BACKWARDS COMPATIBILITY EXPORTS
// (For existing App.jsx that expects math-focused structure)
// =============================================================================
export const testForms = mathIndex.testFormsList || [];
export const practiceCategories = mathPractice.practiceCategories || [];

export function getRandomQuestions(count, categoryId = null) {
  // Delegates to math practice bank
  return mathPractice.getRandomQuestions?.(count, categoryId) || [];
}

export function calculateScaledScore(rawScore, totalQuestions) {
  return mathPractice.calculateScaledScore?.(rawScore, totalQuestions) || rawScore;
}

// =============================================================================
// COMBINED STATISTICS
// =============================================================================
export const stats = {
  totalQuestions: 0,
  bySubject: {
    math: { practice: 0, test: 0, forms: 0 },
    english: { practice: 0, test: 0, forms: 0 },
    reading: { practice: 0, test: 0, forms: 0 },
    science: { practice: 0, test: 0, forms: 0 }
  }
};

// Calculate stats
try {
  // Math - get actual practice bank count
  const mathPracticeStats = mathPractice.stats || { totalQuestions: 1000 };
  stats.bySubject.math.forms = 7; // 2176CPRE, 25MC1, 25MC2, 25MC3, 25MC4, 25MC5, J08
  stats.bySubject.math.test = 326; // 60+45+45+45+45+45+41 questions
  stats.bySubject.math.practice = mathPracticeStats.totalQuestions || 1000;
  
  // English (3 forms: 25MC1, 25MC4, and J08)
  stats.bySubject.english.forms = 3;
  stats.bySubject.english.test = 140; // 50 + 50 + 40 questions
  
  // Reading (2 forms: 25MC1 and J08)
  stats.bySubject.reading.forms = 2;
  stats.bySubject.reading.test = 63; // 36 + 27 questions
  
  // Science (2 forms: 25MC1 and J08)
  stats.bySubject.science.forms = 2;
  stats.bySubject.science.test = 74; // 40 + 34 questions
  
  // Total
  stats.totalQuestions = 
    stats.bySubject.math.practice + stats.bySubject.math.test +
    stats.bySubject.english.test +
    stats.bySubject.reading.test +
    stats.bySubject.science.test;
    
} catch (e) {
  console.warn('Error calculating stats:', e);
}

console.log('=== ACT Prep Data Index Loaded ===');
console.log(`Math: ${stats.bySubject.math.forms} forms, ${stats.bySubject.math.test} test Qs, ${stats.bySubject.math.practice} practice Qs`);
console.log(`English: ${stats.bySubject.english.forms} forms, ${stats.bySubject.english.test} test Qs`);
console.log(`Reading: ${stats.bySubject.reading.forms} forms, ${stats.bySubject.reading.test} test Qs`);
console.log(`Science: ${stats.bySubject.science.forms} forms, ${stats.bySubject.science.test} test Qs`);
console.log(`Total: ${stats.totalQuestions} questions available`);

// =============================================================================
// DEFAULT EXPORT
// =============================================================================
export default {
  subjects,
  categoriesBySubject,
  testFormsBySubject,
  allTestForms,
  getTestQuestions,
  getScoredQuestions,
  calculateTestScore,
  hasRealQuestions,
  getFormMetadata,
  getPassages,
  getTestForms,
  getCategoryName,
  stats
};
